/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { TableSortOrder as SortOrder } from '@atlaskit/custom-steps/constants';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import { messages } from '@atlaskit/editor-common/messages/table';
import { getSelectionRect } from '@atlaskit/editor-tables/utils/get-selection-rect';
import { InformationCircleIcon } from '@atlaskit/editor-toolbar/information-circle-icon';
import { SortAscendingIcon } from '@atlaskit/editor-toolbar/sort-ascending-icon';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { closeActiveTableMenu } from '../../../../pm-plugins/commands';
import { sortColumnWithAnalytics } from '../../../../pm-plugins/commands/commands-with-analytics';
import { useTableMenuContext } from '../../shared/TableMenuContext';
import type { TableMenuComponentsParams } from '../../shared/types';

const styles = cssMap({
	// Wrapper around the info icon so it provides a stable tooltip trigger and
	// a comfortable hover target.
	infoIconTrigger: {
		display: 'inline-flex',
		alignItems: 'center',
	},
});

export const SortIncreasingItem = ({ api }: TableMenuComponentsParams): React.JSX.Element => {
	const tableMenuContext = useTableMenuContext();
	const { editorView } = tableMenuContext ?? {};
	const { formatMessage } = useIntl();
	const hasMergedCellsInTable = Boolean(tableMenuContext?.hasMergedCellsInTable);

	const handleClick = () => {
		if (!editorView) {
			return;
		}
		const selectionRect = getSelectionRect(editorView.state.selection);
		const columnIndex = selectionRect?.left;
		if (columnIndex === undefined) {
			return;
		}
		sortColumnWithAnalytics(api?.analytics?.actions)(
			INPUT_METHOD.TABLE_CONTEXT_MENU,
			columnIndex,
			SortOrder.ASC,
		)(editorView.state, editorView.dispatch);
		api?.core.actions.execute(closeActiveTableMenu(api));
		api?.core.actions.focus();
	};

	// When the table has merged cells, the item remains interactive but is rendered
	// with a disabled-looking label and leading icon, alongside an enabled info icon
	// that surfaces why sorting is unavailable.
	if (hasMergedCellsInTable && fg('platform_editor_table_menu_updates_patch_1')) {
		return (
			<ToolbarDropdownItem
				elemBefore={
					<SortAscendingIcon label="" size="small" color={token('color.text.disabled')} />
				}
				elemAfter={
					<Tooltip content={formatMessage(messages.canNotSortTableNoIcon)} position="top">
						{(tooltipProps) => (
							// Ignored via go/ees005
							// eslint-disable-next-line react/jsx-props-no-spreading
							<Box as="span" xcss={styles.infoIconTrigger} {...tooltipProps}>
								<InformationCircleIcon label="" size="small" color={token('color.icon')} />
							</Box>
						)}
					</Tooltip>
				}
			>
				<Text color="color.text.disabled">{formatMessage(messages.sortColumnIncreasing)}</Text>
			</ToolbarDropdownItem>
		);
	}

	return (
		<ToolbarDropdownItem
			onClick={handleClick}
			isDisabled={Boolean(tableMenuContext?.hasMergedCellsInTable)}
			elemBefore={<SortAscendingIcon label="" size="small" />}
		>
			{formatMessage(messages.sortColumnIncreasing)}
		</ToolbarDropdownItem>
	);
};
